// ============================================
// LOGINMODAL.JS - Modal de Login/Registro
// Conectado a Medusa v2 API
// ============================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import FloatingInput from './FloatingInput';
import SuccessModal from './SuccessModal';
import { MEDUSA_API_URL, MEDUSA_API_KEY } from 'services/medusa/config';
import { useCustomer } from 'context/CustomerContext';
import { lockScroll, unlockScroll } from 'utils/scrollLock';
import { devWarn, devError } from 'utils/devLog';
import './LoginModal.css';
import { SKIN_ATTRS } from '../../data/skinProfile';

// Atributos de piel: compartidos con la activación de cuentas (data/skinProfile).

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const { setSession } = useCustomer();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: '',
    gender: '',
    sebum: '',
    ageRange: '',
    sensitive: '',
    skinType: '',
  });

  // Estados para el SuccessModal
  const [showSuccess, setShowSuccess] = useState(false);
  const [loggedUserName, setLoggedUserName] = useState('');

  // Toggle "Mostrar contraseña" para ambos campos
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Para evitar cierres accidentales al arrastrar desde un input al overlay:
  // solo cerramos si mousedown Y mouseup ocurren sobre el overlay (no el modal).
  const [overlayMouseDownTarget, setOverlayMouseDownTarget] = useState(null);

  // Cierre animado en mobile: reproduce el slide-up (ultra rápido) antes de
  // desmontar. En desktop cierra directo.
  const [closing, setClosing] = useState(false);
  const requestClose = () => {
    if (isMobile) {
      setClosing(true);
      setTimeout(() => {
        setClosing(false);
        onClose();
      }, 170);
    } else {
      onClose();
    }
  };
  // Al reabrir, limpiar el estado de cierre por si quedó colgado.
  useEffect(() => {
    if (isOpen) setClosing(false);
  }, [isOpen]);

  // Detectar mobile para animación
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Bloquear scroll cuando modal está abierto
  useEffect(() => {
    if (isOpen) {
      lockScroll();
    } else {
      unlockScroll();
    }
    return () => { unlockScroll(); };
  }, [isOpen]);

  // Cerrar con ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        requestClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onClose, isMobile]);

  // Limpiar error cuando cambia el modo
  useEffect(() => {
    setError('');
  }, [isLogin]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  // Helper para parsear errores de Medusa v2
  const parseMedusaError = async (response) => {
    try {
      const data = await response.json();
      return data.message || data.error || 'Error desconocido';
    } catch {
      return `Error ${response.status}`;
    }
  };

  // Helper para headers comunes de Medusa
  const medusaHeaders = (token = null) => {
    const headers = {
      'Content-Type': 'application/json',
      'x-publishable-api-key': MEDUSA_API_KEY,
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  // Medusa v2: tras POST /store/customers el token original es "auth-identity",
  // necesitamos refrescarlo para que incluya el customer_id y /store/customers/me funcione.
  const refreshAuthToken = async (currentToken) => {
    try {
      const res = await fetch(`${MEDUSA_API_URL}/auth/token/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: medusaHeaders(currentToken),
      });
      if (!res.ok) return currentToken;
      const data = await res.json();
      const newToken = data?.token;
      if (newToken) {
        localStorage.setItem('medusa_token', newToken);
        return newToken;
      }
      return currentToken;
    } catch {
      return currentToken;
    }
  };

  // Registrar nuevo cliente en Medusa v2
  const registerCustomer = async () => {
    // Paso 1: Crear identidad de autenticación
    const registerResponse = await fetch(`${MEDUSA_API_URL}/auth/customer/emailpass/register`, {
      method: 'POST',
      credentials: 'include',
      headers: medusaHeaders(),
      body: JSON.stringify({
        email: formData.email,
        password: formData.password,
      }),
    });

    if (!registerResponse.ok) {
      const msg = await parseMedusaError(registerResponse);
      throw new Error(msg === 'Identity with email already exists'
        ? 'Este correo ya está registrado. Inicia sesión.'
        : msg);
    }

    const registerData = await registerResponse.json();
    let token = registerData.token;
    if (!token) throw new Error('No se recibió token de autenticación');

    localStorage.setItem('medusa_token', token);

    // Paso 2: CREAR el registro de cliente (POST /store/customers, no /me)
    // Medusa v2 requiere el email explícito para enlazarlo con la auth-identity.
    const createResponse = await fetch(`${MEDUSA_API_URL}/store/customers`, {
      method: 'POST',
      credentials: 'include',
      headers: medusaHeaders(token),
      body: JSON.stringify({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        // Género (saludo) + atributos de piel para segmentar reseñas futuras.
        metadata: {
          gender: formData.gender || 'x',
          ...(formData.sebum ? { sebum: formData.sebum } : {}),
          ...(formData.ageRange ? { age_range: formData.ageRange } : {}),
          ...(formData.sensitive ? { sensitive_skin: formData.sensitive } : {}),
          ...(formData.skinType ? { skin_type: formData.skinType } : {}),
        },
      }),
    });

    if (!createResponse.ok) {
      const errMsg = await parseMedusaError(createResponse);
      // Si el cliente ya existe (400), continuar al GET /me
      if (createResponse.status === 400) {
        devWarn('Perfil ya existe o error al crear, obteniendo existente...', errMsg);
      } else {
        throw new Error(errMsg);
      }
    }

    // Paso 2b: Refrescar token para que incluya el customer_id recién asociado
    token = await refreshAuthToken(token);

    // Paso 3: Obtener datos del cliente recién creado
    const meResponse = await fetch(`${MEDUSA_API_URL}/store/customers/me`, {
      method: 'GET',
      credentials: 'include',
      headers: medusaHeaders(token),
    });

    if (meResponse.ok) {
      const meData = await meResponse.json();
      return meData.customer;
    }

    // Fallback: si GET /me falla, intentar login con las credenciales
    devWarn('GET /me falló tras registro, intentando login directo...');
    const loginData = await loginCustomer();
    if (loginData) return loginData;

    // Último fallback: datos del formulario
    return {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
    };
  };

  // Login con Medusa v2
  const loginCustomer = async () => {
    // Paso 1: Autenticar con email/password
    const authResponse = await fetch(`${MEDUSA_API_URL}/auth/customer/emailpass`, {
      method: 'POST',
      credentials: 'include',
      headers: medusaHeaders(),
      body: JSON.stringify({
        email: formData.email,
        password: formData.password,
      }),
    });

    if (!authResponse.ok) {
      const msg = await parseMedusaError(authResponse);
      if (authResponse.status === 401 || authResponse.status === 400) {
        throw new Error('Correo o contraseña incorrectos');
      }
      throw new Error(msg);
    }

    const authData = await authResponse.json();
    let token = authData.token;
    if (!token) throw new Error('No se recibió token de autenticación');

    localStorage.setItem('medusa_token', token);

    // Paso 2: Obtener datos del cliente
    const customerResponse = await fetch(`${MEDUSA_API_URL}/store/customers/me`, {
      method: 'GET',
      credentials: 'include',
      headers: medusaHeaders(token),
    });

    if (customerResponse.ok) {
      const customerData = await customerResponse.json();
      return customerData.customer;
    }

    // Si no existe el registro de cliente, intentar crearlo
    if (customerResponse.status === 401 || customerResponse.status === 404 || customerResponse.status === 400) {
      try {
        const createResponse = await fetch(`${MEDUSA_API_URL}/store/customers`, {
          method: 'POST',
          credentials: 'include',
          headers: medusaHeaders(token),
          body: JSON.stringify({}),
        });

        if (createResponse.ok) {
          // Refrescar token para asociar customer_id y reintentar /me
          token = await refreshAuthToken(token);
          const retryMe = await fetch(`${MEDUSA_API_URL}/store/customers/me`, {
            method: 'GET',
            credentials: 'include',
            headers: medusaHeaders(token),
          });
          if (retryMe.ok) {
            const retryData = await retryMe.json();
            return retryData.customer;
          }
          const created = await createResponse.json();
          return created.customer;
        }
      } catch (e) {
        // Silenciar error de creación
      }
    }

    // Fallback: usar datos del formulario cuando la API no responde correctamente
    return {
      email: formData.email,
      first_name: formData.firstName || formData.email.split('@')[0],
      last_name: formData.lastName || '',
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validar contraseñas en registro
      if (!isLogin && formData.password !== formData.confirmPassword) {
        throw new Error('Las contraseñas no coinciden');
      }

      let customer;

      if (isLogin) {
        customer = await loginCustomer();
      } else {
        customer = await registerCustomer();
      }

      // Centralizar sesión via CustomerContext
      const token = localStorage.getItem('medusa_token');
      const customerData = {
        ...customer,
        first_name: customer.first_name || formData.firstName,
        last_name: customer.last_name || formData.lastName,
        email: customer.email || formData.email,
      };
      setSession(token, customerData);

      // Preparar nombre completo para el SuccessModal
      const fullName = `${customer.first_name || formData.firstName} ${customer.last_name || formData.lastName}`.trim();
      setLoggedUserName(fullName);

      // Cerrar LoginModal y mostrar SuccessModal
      onClose();
      setShowSuccess(true);

      // Callback para notificar al padre
      if (onLoginSuccess) {
        onLoginSuccess(customer);
      }

      // Limpiar formulario
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        confirmPassword: '',
      });

    } catch (err) {
      devError('Auth error:', err);
      setError(err.message || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      confirmPassword: '',
      gender: '',
      sebum: '',
      ageRange: '',
      sensitive: '',
      skinType: '',
    });
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
  };

  const handleGoToAccount = () => {
    setShowSuccess(false);
    navigate('/account');
  };

  const handleContinueShopping = () => {
    setShowSuccess(false);
    // Solo cerrar, ya está en la página actual
  };

  return (
    <>
      {isOpen && (
        <div
          className="login-overlay"
          onMouseDown={(e) => setOverlayMouseDownTarget(e.target)}
          onMouseUp={(e) => {
            // Solo cerrar si el gesto completo (down y up) ocurrió en el overlay.
            // Evita cierres accidentales al seleccionar texto en un input y soltar fuera.
            if (
              overlayMouseDownTarget &&
              overlayMouseDownTarget === e.currentTarget &&
              e.target === e.currentTarget
            ) {
              requestClose();
            }
            setOverlayMouseDownTarget(null);
          }}
        >
          <div
            className={`login-modal ${isMobile ? 'mobile' : 'desktop'} ${closing ? 'is-closing' : ''}`}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button className="login-modal__close" onClick={requestClose} aria-label="Cerrar">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>

            {/* Header */}
            <div className="login-modal__header">
              <h2 className="login-modal__title">
                {isLogin ? 'Iniciar sesión' : 'Crear cuenta'}
              </h2>
              <p className="login-modal__subtitle">
                {isLogin ? 'Accede a tu cuenta de Physiology' : 'Únete a la comunidad Physiology'}
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="login-modal__error">
                {error}
              </div>
            )}

            {/* Form */}
            <form className="login-modal__form" onSubmit={handleSubmit}>
              {!isLogin && (
                <>
                  <FloatingInput
                    type="text"
                    name="firstName"
                    label="Nombre"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                  <FloatingInput
                    type="text"
                    name="lastName"
                    label="Apellido"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                  <div className="login-modal__field">
                    <label className="login-modal__field-label" htmlFor="gender">
                      Género <span className="login-modal__field-hint">(para dirigirnos a ti correctamente)</span>
                    </label>
                    <select
                      id="gender"
                      name="gender"
                      className="login-modal__select"
                      value={formData.gender}
                      onChange={handleChange}
                      disabled={loading}
                    >
                      <option value="">Prefiero no decirlo</option>
                      <option value="f">Femenino</option>
                      <option value="m">Masculino</option>
                    </select>
                  </div>

                  <div className="login-modal__attrs">
                    <span className="login-modal__field-label">
                      Sobre tu piel <span className="login-modal__field-hint">(opcional · para recomendaciones a tu medida)</span>
                    </span>
                    <div className="login-modal__attrs-grid">
                      {SKIN_ATTRS.map((a) => (
                        <select
                          key={a.name}
                          name={a.name}
                          className="login-modal__attr-select"
                          value={formData[a.name]}
                          onChange={handleChange}
                          disabled={loading}
                          aria-label={a.label}
                        >
                          <option value="">{a.label}</option>
                          {a.options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <FloatingInput
                type="email"
                name="email"
                label="Correo electrónico"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
              />

              <FloatingInput
                type={showPassword ? 'text' : 'password'}
                name="password"
                label="Contraseña"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
              />
              <label className="login-modal__toggle-pw">
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                  disabled={loading}
                />
                <span>Mostrar contraseña</span>
              </label>

              {!isLogin && (
                <>
                  <FloatingInput
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    label="Confirmar contraseña"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                  <label className="login-modal__toggle-pw">
                    <input
                      type="checkbox"
                      checked={showConfirmPassword}
                      onChange={(e) => setShowConfirmPassword(e.target.checked)}
                      disabled={loading}
                    />
                    <span>Mostrar contraseña</span>
                  </label>
                </>
              )}

              {/* Forgot password link (solo en login) */}
              {isLogin && (
                <Link
                  to="/forgot-password"
                  onClick={onClose}
                  className="login-modal__forgot"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              )}

              {/* Submit button */}
              <button 
                type="submit" 
                className="login-modal__submit"
                disabled={loading}
              >
                {loading ? 'Procesando...' : (isLogin ? 'Iniciar sesión' : 'Crear cuenta')}
              </button>
            </form>

            {/* Switch mode */}
            <div className="login-modal__switch">
              <p>
                {isLogin ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}{' '}
                <button 
                  type="button" 
                  onClick={switchMode} 
                  className="login-modal__switch-btn"
                  disabled={loading}
                >
                  {isLogin ? 'Regístrate' : 'Inicia sesión'}
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SuccessModal - se muestra después del login exitoso */}
      <SuccessModal
        isOpen={showSuccess}
        onClose={handleSuccessClose}
        userName={loggedUserName}
        onGoToAccount={handleGoToAccount}
        onContinueShopping={handleContinueShopping}
      />
    </>
  );
};

export default LoginModal;
